import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FeaturePageShell } from "../features/_Shell";
import { cardBorderClassName } from "../../lib/cardStyles";
import { BUSINESS_TYPES } from "../../lib/businessDefaults";
import { getOrCreateOnboarding, saveOnboarding } from "../../lib/onboarding";
import { DEMO_BOOKING_URL, TRIAL_DAYS } from "../../lib/pricing";
import { usePageTitle } from "../../lib/usePageTitle";

const FONT = { fontFamily: "Space Grotesk, sans-serif" };
const PANEL_BG = { background: "rgba(28, 28, 37, 0.7)" };
const MAX_PARAM = 120;

/**
 * Link format reps send after a demo (all params optional):
 *   /trial?agency=Echeveste%20Insurance&name=Luis%20Echeveste&email=luis%40example.com&type=insurance
 * type must be a BUSINESS_TYPES id; anything else is ignored.
 */
function readParam(params: URLSearchParams, key: string): string {
  return (params.get(key) || "").trim().slice(0, MAX_PARAM);
}

const STEPS = [
  {
    n: "01",
    title: "Set up your receptionist",
    body: "Pick your business type, name your receptionist, and set its greeting. About 5 minutes.",
  },
  {
    n: "02",
    title: "Add a card",
    body: `Nothing is charged today. Your trial runs ${TRIAL_DAYS} days.`,
  },
  {
    n: "03",
    title: "Pick a local number",
    body: "Choose a number in your area code for your receptionist.",
  },
  {
    n: "04",
    title: "Call it",
    body: "Call your new number and hear it answer. Then add your hours and common questions in the dashboard.",
  },
];

export default function TrialPage() {
  usePageTitle("Free trial");
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const prefill = useMemo(() => {
    const type = readParam(params, "type");
    return {
      agency: readParam(params, "agency"),
      name: readParam(params, "name"),
      email: readParam(params, "email"),
      type: BUSINESS_TYPES.some((b) => b.id === type) ? type : "",
    };
  }, [params]);

  const firstName = prefill.name.split(/\s+/)[0] || "";

  function startTrial() {
    const ob = getOrCreateOnboarding("starter");
    const step2 = { ...(ob.step2 || {}) };
    if (prefill.agency) step2.businessName = prefill.agency;
    if (prefill.name) step2.fullName = prefill.name;
    if (prefill.email) step2.email = prefill.email;
    saveOnboarding({
      ...ob,
      plan: "starter",
      ...(prefill.type ? { businessType: prefill.type } : {}),
      step2,
    });
    navigate(prefill.type ? "/onboarding/2" : "/onboarding/1");
  }

  return (
    <FeaturePageShell>
      {/* Hero */}
      <section className="flex flex-col items-center pt-20 md:pt-24 pb-14 px-6 text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-orange-300 mb-4" style={FONT}>
          {prefill.agency ? `Free trial for ${prefill.agency}` : "Free trial"}
        </p>
        <h1
          className="text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent mb-6 max-w-4xl"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          {firstName ? `${firstName}, try it free for ${TRIAL_DAYS} days` : `Try NeuroSphere free for ${TRIAL_DAYS} days`}
        </h1>
        <p className="text-lg md:text-xl text-gray-300 max-w-3xl tracking-wide mb-10" style={FONT}>
          Set up your AI receptionist, pick a local number, and call it yourself. Add a card to start. You won't be
          charged until your trial ends.
        </p>
        <button
          type="button"
          onClick={startTrial}
          className="px-10 py-4 rounded-lg bg-gradient-to-r from-purple-500 to-orange-400 hover:from-purple-600 hover:to-orange-500 transition-all duration-300 text-white font-semibold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 whitespace-nowrap cursor-pointer"
          style={FONT}
        >
          Start my free trial
        </button>
        <p className="mt-4 text-sm text-gray-400" style={FONT}>
          Takes about 5 minutes. Card required. Nothing charged today.
        </p>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center" style={FONT}>
          How your trial works
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s) => (
            <div key={s.n} className={`rounded-xl p-6 ${cardBorderClassName}`} style={PANEL_BG}>
              <div
                className="text-3xl font-extrabold bg-gradient-to-r from-purple-400 to-orange-400 bg-clip-text text-transparent mb-3"
                style={{ fontFamily: "Orbitron, sans-serif" }}
              >
                {s.n}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" style={FONT}>
                {s.title}
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm" style={FONT}>
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* After the trial */}
      <section className="max-w-3xl mx-auto px-6 pb-20">
        <div className={`rounded-xl p-6 md:p-8 ${cardBorderClassName}`} style={PANEL_BG}>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-4" style={FONT}>
            After {TRIAL_DAYS} days
          </h2>
          <ul className="space-y-3 text-gray-300" style={FONT}>
            <li className="flex gap-3">
              <i className="ri-checkbox-circle-line text-purple-400 mt-0.5" aria-hidden="true" />
              <span>Your plan starts at $299 a month, month to month. No annual contract.</span>
            </li>
            <li className="flex gap-3">
              <i className="ri-checkbox-circle-line text-purple-400 mt-0.5" aria-hidden="true" />
              <span>Not the right fit? Tell our team before your trial ends and you won't be charged.</span>
            </li>
            <li className="flex gap-3">
              <i className="ri-checkbox-circle-line text-purple-400 mt-0.5" aria-hidden="true" />
              <span>
                Want help setting it up?{" "}
                <a
                  href={DEMO_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-300 underline hover:text-purple-200"
                >
                  Book a time with us
                </a>
                .
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section className="flex flex-col items-center pb-24 px-6 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" style={FONT}>
          Hear it answer your calls
        </h2>
        <button
          type="button"
          onClick={startTrial}
          className="px-10 py-4 rounded-lg bg-gradient-to-r from-purple-500 to-orange-400 hover:from-purple-600 hover:to-orange-500 transition-all duration-300 text-white font-semibold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 whitespace-nowrap cursor-pointer"
          style={FONT}
        >
          Start my free trial
        </button>
        <Link to="/login" className="mt-6 text-sm text-gray-400 hover:text-gray-300" style={FONT}>
          Already started? Log in
        </Link>
      </section>
    </FeaturePageShell>
  );
}
